import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  Autocomplete,
} from '@mui/material';
import { professionalSchema } from '../../schemas/validationSchemas';
import { getAllTitles } from '../../utils/professionalNameUtils';

/**
 * Formulário de Profissional (Create/Update) com validação Zod
 */
// focusField: campo que recebe o foco quando a janela termina de abrir (ex.: 'specialty'). A tela
// de Profissionais usa para o "complete o perfil": cada item abre a edição já no campo que falta.
export default function ProfessionalForm({ open, onClose, onSubmit, data, loading = false, focusField = null }) {
  const isEdit = !!data;
  const tituloRef = useRef(null); // o título é um Autocomplete (setValue), fora do register

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setFocus,
    watch,
    formState: { errors, isValid, isDirty },
  } = useForm({
    resolver: zodResolver(professionalSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      title: '',
      specialty: '',
      registrationNumber: '',
      phone: '',
      email: '',
      description: '',
      bio: '',
    },
  });

  const titleValue = watch('title');

  useEffect(() => {
    if (open) {
      if (data) {
        reset({
          name: data.name || '',
          title: data.title || '',
          specialty: data.specialty || '',
          registrationNumber: data.registrationNumber || '',
          phone: data.phone || '',
          email: data.email || '',
          description: data.description || '',
          bio: data.bio || '',
        });
      } else {
        reset({
          name: '',
          title: '',
          specialty: '',
          registrationNumber: '',
          phone: '',
          email: '',
          description: '',
          bio: '',
        });
      }
    }
  }, [data, open, reset]);

  const onSubmitForm = (formData) => {
    const apiData = {
      name: formData.name,
      title: formData.title || null,
      specialty: formData.specialty || null,
      registrationNumber: formData.registrationNumber || null,
      phone: formData.phone || null,
      email: formData.email || null,
      description: formData.description || null,
      bio: formData.bio || null,
    };
    onSubmit(apiData);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{
        onEntered: () => {
          if (focusField === 'title') tituloRef.current?.focus();
          else if (focusField) setFocus(focusField);
        },
      }}
    >
      <DialogTitle>
        {isEdit ? 'Editar Profissional' : 'Novo Profissional'}
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmitForm)}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Nome"
              fullWidth
              {...register('name')}
              error={!!errors.name}
              helperText={errors.name?.message}
              required
              disabled={loading}
              autoFocus={!focusField}
            />

            <Autocomplete
              freeSolo
              options={getAllTitles()}
              value={titleValue || ''}
              onChange={(event, newValue) => {
                setValue('title', newValue || '', { shouldValidate: true, shouldDirty: true });
              }}
              onInputChange={(event, newInputValue) => {
                setValue('title', newInputValue || '', { shouldValidate: true, shouldDirty: true });
              }}
              disabled={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={tituloRef}
                  label="Título"
                  helperText="Ex: Dr., Profa., Eng. (opcional)"
                  error={!!errors.title}
                />
              )}
            />

            <TextField
              label="Especialidade"
              fullWidth
              {...register('specialty')}
              error={!!errors.specialty}
              helperText={errors.specialty?.message || 'Ex: Dentista, Psicólogo, Médico'}
              disabled={loading}
            />

            <TextField
              label="Número de Registro"
              fullWidth
              {...register('registrationNumber')}
              error={!!errors.registrationNumber}
              helperText={errors.registrationNumber?.message || 'Ex: CRM, CRO, CRP'}
              disabled={loading}
            />

            <TextField
              label="Telefone"
              fullWidth
              {...register('phone')}
              error={!!errors.phone}
              helperText={errors.phone?.message}
              disabled={loading}
            />

            <TextField
              label="Email"
              fullWidth
              type="email"
              {...register('email')}
              error={!!errors.email}
              helperText={errors.email?.message}
              disabled={loading}
            />

            <TextField
              label="Descrição"
              fullWidth
              multiline
              rows={2}
              {...register('description')}
              error={!!errors.description}
              helperText={errors.description?.message || 'Descrição breve do profissional'}
              disabled={loading}
            />

            <TextField
              label="Biografia"
              fullWidth
              multiline
              rows={3}
              {...register('bio')}
              error={!!errors.bio}
              helperText={errors.bio?.message || 'Informações detalhadas sobre o profissional'}
              disabled={loading}
            />

            {Object.keys(errors).length > 0 && (
              <Alert severity="error">
                Por favor, corrija os erros acima antes de salvar.
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={loading || !isValid || (!isDirty && !isEdit)}>
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
